/**
 * ExpertCard Component
 * Matches Figma design: 520x340 card, rounded-[20px], dark glass bg,
 * 90px square avatar, name+handle inline, action icons top-right,
 * bio text, bordered tag chips, sessions count, cream CTA button.
 *
 * Uses text-text-primary / text-text-body which resolve correctly in .dark context.
 */

import { Calendar } from "lucide-react"
import { useNavigate } from "react-router-dom"
import { ChatBubbleIcon, HeartFilledIcon, HeartOutlineIcon } from "@/components/icons"
import { formatFollowerCount } from "@/components/shared/formatFollowerCount"
import { formatLocation } from "@/components/shared/formatLocation"
import { TagList } from "@/components/shared/TagList"
import { Button } from "@/components/ui/button"
import { useCreateDmConversation } from "@/features/chats/hooks/useDmChat"
import type { Expert } from "../types/connect.types"

interface ExpertCardProps {
  expert: Expert
  onFollow?: (userId: string) => void
  onUnfollow?: (userId: string) => void
  className?: string
}

export function ExpertCard({ expert, onFollow, onUnfollow, className }: ExpertCardProps) {
  const navigate = useNavigate()
  const { mutate: createConversation, isPending: isCreatingConversation } =
    useCreateDmConversation()
  const avatarFallback = expert.name?.[0] || "?"
  const location = formatLocation(expert.city, expert.state)

  return (
    <div
      className={`bg-white/5 flex flex-col justify-between border border-white/10 rounded-[20px] overflow-hidden relative p-5 flex flex-col max-h-[342px] ${className || ""}`}
    >
      {/* Top row: Avatar + Name/Handle + Action Icons */}
      <div className="relative flex items-start gap-5">
        {/* Avatar - 90px square with rounded-xl */}
        <button
          type="button"
          onClick={() => navigate(`/profile/${expert.id}`)}
          className="shrink-0 cursor-pointer hover:opacity-80 transition-opacity cursor-pointer"
          aria-label={`View ${expert.name}'s profile`}
        >
          {expert.profilePictureUrl ? (
            <img
              src={expert.profilePictureUrl}
              alt={expert.name}
              loading="lazy"
              className="w-[90px] h-[90px] object-cover rounded-xl"
            />
          ) : (
            <div className="w-[90px] h-[90px] rounded-xl bg-beige-medium flex items-center justify-center text-charcoal text-2xl font-medium">
              {avatarFallback}
            </div>
          )}
        </button>

        {/* Name + Handle + Followers/Location */}
        <div
          className="flex-1 min-w-0 pt-1.5 cursor-pointer"
          onClick={() => navigate(`/profile/${expert.id}`)}
        >
          <div className="flex flex-col items-start gap-1">
            <span className="text-[18px] font-semibold text-text-primary leading-[22px] truncate">
              {expert.name}
            </span>
            <span className="text-[14px] text-text-primary/90 leading-[22px] truncate">
              @{expert.username}
            </span>
          </div>
          <div className="flex items-center gap-2.5 mt-2 text-[13px] text-text-primary/70 leading-[20px]">
            <span>{formatFollowerCount(expert.followerCount)} followers</span>
            {location && <span>{location}</span>}
          </div>
        </div>

        {/* Action icons - top right */}
        <div className="absolute flex items-center gap-2.5 shrink-0 -top-0 right-0">
          <button
            type="button"
            className="w-11 h-11 rounded-[10px] bg-connect-glass-bg border border-connect-glass-border flex items-center justify-center hover:bg-white/10 transition-colors cursor-pointer disabled:opacity-50"
            onClick={e => {
              e.stopPropagation()
              createConversation(expert.id, {
                onSuccess: conversation => {
                  navigate(`/chats/dm/${conversation.id}`)
                },
              })
            }}
            disabled={isCreatingConversation}
            title="Chat"
          >
            <ChatBubbleIcon className="w-[22px] h-[22px] text-text-primary/70" />
          </button>
          <button
            type="button"
            onClick={e => {
              e.stopPropagation()
              expert.isFollowing ? onUnfollow?.(expert.id) : onFollow?.(expert.id)
            }}
            className="w-11 h-11 rounded-[10px] bg-connect-glass-bg border border-connect-glass-border flex items-center justify-center hover:bg-white/10 transition-colors cursor-pointer"
            aria-label={expert.isFollowing ? "Unfollow" : "Follow"}
          >
            {expert.isFollowing ? (
              <HeartFilledIcon className="w-[22px] h-[22px] text-red-500" />
            ) : (
              <HeartOutlineIcon className="w-[22px] h-[22px] text-text-primary/70" />
            )}
          </button>
        </div>
      </div>

      {/* Bio */}
      {expert.bio && (
        <p className="text-sm text-text-primary leading-[25px] line-clamp-2 mt-5">{expert.bio}</p>
      )}

      {/* Tags */}
      <TagList tags={expert.tags} className="mt-5" />

      {/* Bottom row: Sessions + Book CTA */}
      <div className="flex items-center justify-between mt-7.5">
        <p className="text-sm text-text-primary leading-[20px]">
          {expert.sessionsAvailable} Sessions Available
        </p>
        <Button
          variant="outline"
          type="button"
          onClick={() => navigate(`/profile/${expert.id}#sessions`)}
          className="h-[50px] pb-3 pt-3.25 bg-transparent rounded-full flex items-center gap-2 leading-[20px] transition-colors border-[#F2F0EB]"
        >
          <Calendar className="w-4.5 h-4.5" />
          <span className="text-[14px] font-semibold uppercase ">Book a Session</span>
        </Button>
      </div>
    </div>
  )
}
